import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect, DispatchProp } from 'react-redux';
import { NavLink, RouteComponentProps } from 'react-router-dom';
import { Col, Collapse, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';

import { HSpacer } from 'src/components/core/Spacer';
import { VStack } from 'src/components/core/Stack';
import { Button } from 'src/components/Insights/Common/Button';
import authSelectors from 'src/redux/auth/auth-selectors';
import { EditTransaction, editUser, logout } from 'src/redux/auth/auth-slice';
import { StoreState } from 'src/redux/store';
import { User } from 'src/types/auth';
import { FeatureFlags } from 'src/types/core';
import dateFormatter from 'src/util/date';
import { redirectUserToConversations } from 'src/util/urls';
import { getFlag } from 'src/util/user';
import Layout from '../Layout/Layout';
import ChangePassword from './ChangePassword/ChangePassword';
import ProfileSettings from './ProfileSettings/ProfileSettings';
import UserPreferences from './UserPreferences/UserPreferences';

import styles from './SettingsPage.module.scss';

interface StateProps {
  user: User;
  chapterTransaction?: EditTransaction;
  languageTransaction?: EditTransaction;
  profileTransaction?: EditTransaction;
  profileImageTransaction?: EditTransaction;
  passwordTransaction?: EditTransaction;
}

type Props = RouteComponentProps & StateProps & DispatchProp;

/** Map state from redux to the components props */
const mapStateToProps = (state: StoreState): StateProps => ({
  user: authSelectors.getUser(state),
  chapterTransaction: authSelectors.getEditUserTransaction(state, 'chapter'),
  profileTransaction: authSelectors.getEditUserTransaction(state, 'profile'),
  profileImageTransaction: authSelectors.getEditUserTransaction(
    state,
    'profile_image'
  ),
  languageTransaction: authSelectors.getEditUserTransaction(state, 'language'),
  passwordTransaction: authSelectors.getEditUserTransaction(state, 'password'),
});

/**
 * User preferences page
 */
const SettingsPageContent = ({
  user,
  chapterTransaction,
  profileTransaction,
  profileImageTransaction,
  languageTransaction,
  passwordTransaction,
  dispatch,
}: Props) => {
  const { t } = useTranslation();
  const [isOpenChangePassword, setIsOpenChangePassword] = React.useState(false);

  const toggleOpenChangePassword = () => {
    setIsOpenChangePassword(!isOpenChangePassword);
  };

  const handleUpdateUser = (editTransaction: EditTransaction) => {
    // set the edit user id so that the state will update
    dispatch(editUser(editTransaction));
  };

  const onClickLogOut = () => {
    dispatch(logout());
  };

  const passwordResetUrl = `/password_reset?next=${redirectUserToConversations(
    window.location.pathname
  )}`;
  const isProjectsPlatform = getFlag(user, FeatureFlags.projects_platform);
  return (
    <>
      <header className="mb-3 flex-row">
        <VStack>
          <h1 className="h2 mb-1 lh-1">{t('settings.header')}</h1>
          <p className="text-muted lead">
            {t('settings.subheader', {
              date: dateFormatter.dateFormat(user.date_joined, {
                includeYear: true,
              }),
            })}
          </p>
        </VStack>

        <HSpacer grow />
        {!isProjectsPlatform && (
          <Button sx={{ alignSelf: 'flex-start' }} onClick={onClickLogOut}>
            {t('main_nav.account_logout')}
          </Button>
        )}
      </header>
      <Row className="mb-5">
        <Col md={8} lg={6}>
          <h2 className=" h3 mb-2">{t('settings.profile')}</h2>
          <ProfileSettings
            user={user}
            onUpdateUser={handleUpdateUser}
            profileTransaction={profileTransaction}
            profileImageTransaction={profileImageTransaction}
          />
        </Col>
      </Row>
      <Row className="mb-5">
        <Col md={8} lg={6}>
          <h2 className="h3 mb-2" data-testid="settings-preference">
            {t('settings.pref')}
          </h2>
          <UserPreferences
            user={user}
            onUpdateUser={handleUpdateUser}
            chapterTransaction={chapterTransaction}
            languageTransaction={languageTransaction}
          />
        </Col>
      </Row>
      <Row className="mb-5">
        <Col md={8} lg={6}>
          {user.has_password ? (
            <>
              <button
                onClick={toggleOpenChangePassword}
                className={cx(styles.stylelessButton, 'mb-3')}
                data-testid="password-collapse"
                aria-expanded={isOpenChangePassword}
              >
                <h3 className="mb-0">
                  <span className="me-2">{t('settings.password_update')}</span>
                  {isOpenChangePassword ? (
                    <FontAwesomeIcon
                      icon={['far', 'chevron-down']}
                      size="sm"
                    ></FontAwesomeIcon>
                  ) : (
                    <FontAwesomeIcon
                      icon={['far', 'chevron-right']}
                      size="sm"
                    />
                  )}
                </h3>
              </button>
              <Collapse isOpen={isOpenChangePassword}>
                <ChangePassword
                  user={user}
                  passwordResetUrl={passwordResetUrl}
                  passwordTransaction={passwordTransaction}
                  onUpdateUser={handleUpdateUser}
                />
              </Collapse>
            </>
          ) : (
            <>
              <h2 id="create-password" className="h3 mb-2">
                {t('register.password_header')}
              </h2>
              <p className="text-gray-700">
                <Trans
                  i18nKey="settings.password_create"
                  /*eslint-disable */
                  components={{
                    1: (
                      <a
                        href={passwordResetUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      />
                    ),
                  }}
                  /*eslint-disable */
                />
              </p>
            </>
          )}
        </Col>
      </Row>

      {!isProjectsPlatform && (
        <section className="border-top pt-4 mt-4">
          <NavLink to="/legal">{t('main_nav.account_legal')}</NavLink>
        </section>
      )}
    </>
  );
};

export const EmbeddedSettingsPage = connect(mapStateToProps)((props: Props) => {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: '16px',
        overflow: 'auto',
      }}
    >
      <SettingsPageContent {...props} />
    </div>
  );
});

const SettingsPage = (props: Props) => {
  const { t } = useTranslation();
  return (
    <Layout className="SettingsPage" title={t('settings.header')}>
      <SettingsPageContent {...props} />
    </Layout>
  );
};

// use connect() to get redux to connect to the component
export default connect(mapStateToProps)(SettingsPage);
